<template>
  <div
    class="editor-quill-wrap"
    :class="{ 'chat-disabled': chatDisabled, 'ai-enabled': aiEnabled }"
    data-cy="view-conversations-editor"
  >
    <template v-if="aiEnabled">
      <ChatConversationAi
        :get-inner-html="getInnerHtml"
        @update="updateWithAi"
        @loading="$emit('loading', $event)"
      />
    </template>
    <div
      id="quill-editor"
      class="quill"
      data-cy="editor"
    />
    <div class="actions">
      <InputFile
        v-model="editorContentLocal.files"
        class="upload"
        :public-id="publicId"
        :use-public-api="isPublic"
        @uploading="uploading"
      />
      <ButtonV2
        big
        class="send-messages"
        data-cy="editor-send"
        :disabled="!canSend"
        :loading="uploadInProgress"
        @click="sendMessage"
      >
        {{ $t("shared.conversations.send") }}
      </ButtonV2>
    </div>
  </div>
</template>

<script>
import Quill from 'quill';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import ButtonV2 from '../ButtonV2.vue';
import InputFile from '../input-file.vue';
import ChatConversationAi from './chat-conversation-ai.vue';

const defaultEditorContent = {
  delta: new (Quill.import('delta'))(),
  files: [],
  selection: undefined,
};

// The quilleditor has to be kept non-reactive to avoid and infinite loop issue
let quillEditor;

export default {
  name: 'ChatConversationEditor',
  components: {
    ButtonV2,
    InputFile,
    ChatConversationAi,
  },
  props: {
    publicId: { type: [String, null], default: null },
    isPublic: { type: Boolean, required: false, default: false },
    editorContent: {
      required: false,
      validator(c) {
        return (
          c === undefined
          || ['files', 'delta', 'selection'].every((prop) => Object.keys(c).includes(prop))
        );
      },
      default: () => defaultEditorContent,
    },
    chatDisabled: { type: Boolean },
    aiEnabled: { type: Boolean, default: false },
  },
  emits: ['editor-content-changed', 'send-message', 'loading'],
  data() {
    return {
      editor: null,
      uploadInProgress: null,
      // need to mutate a copy, not the reference
      editorContentLocal: { ...this.editorContent },
    };
  },
  computed: {
    canSend() {
      return (
        !this.uploadInProgress && (this.editorHasText || this.editorHasFile) && !this.chatDisabled
      );
    },
    editorHasText() {
      return !!this.editorContentLocal.delta.ops.length;
    },
    editorHasFile() {
      return !!this.editorContentLocal.files.length;
    },
  },
  watch: {
    chatDisabled: {
      handler(disabled) {
        if (disabled) {
          quillEditor.disable();
          quillEditor.setText(this.$t('shared.conversations.you_can_no_longer_chat'));
        } else {
          quillEditor.enable();
          quillEditor.setText('');
        }
      },
    },
  },
  mounted() {
    quillEditor = new Quill('#quill-editor', {
      theme: 'snow',
      placeholder: this.$t('shared.conversations.write_message'),
      modules: {
        toolbar: [
          ['bold', 'italic', 'strike'],
          ['link'],
          [{ list: 'ordered' }, { list: 'bullet' }],
        ],
      },
      formats: [
        'bold',
        'italic',
        'link',
        'strike',
        'underline',
        'indent',
        'list',
      ],
    });
    // enforce text direction to be ltr
    quillEditor.format('direction', 'ltr');
    quillEditor.format('align', 'left');

    // @see https://quilljs.com/playground/#autosave
    quillEditor.setContents(this.editorContentLocal.delta);
    quillEditor.setSelection(this.editorContentLocal.selection || Infinity);

    quillEditor.on(Quill.events.TEXT_CHANGE, () => {
      this.editorContentLocal.delta = quillEditor.getContents();
      // when changing the text, we want to remove the old selection or else
      // it could apply to our new text (and that doesn't make sense)
      this.editorContentLocal.selection = undefined;
      this.emitChangedLocalContent();
    });

    quillEditor.on('selection-change', (newSelection) => {
      // destroying the component results in one last selection change that
      // is null - we don't want that one
      if (!newSelection) return;

      this.editorContentLocal.selection = { ...newSelection };
      this.emitChangedLocalContent();
    });

    if (this.chatDisabled) {
      quillEditor.setText(this.$t('shared.conversations.you_can_no_longer_chat'));
      quillEditor.disable();
    }
  },
  methods: {
    getInnerHtml() {
      return quillEditor.root.innerHTML;
    },
    updateWithAi(text) {
      quillEditor.root.innerHTML = text;
    },
    emitChangedLocalContent() {
      this.$emit('editor-content-changed', this.editorContentLocal);
    },
    sendMessage() {
      if (this.chatDisabled) return;
      const text = quillEditor.root.innerHTML;
      this.$emit('send-message', {
        text,
        files: this.editorContentLocal.files.map((file) => file.url.replace(/.*\/(.*\/.*)$/, '$1')),
      });
      // @TODO: This fire and forget is a bit dangerous - if the message could
      // not be sent it is deleted anyway, leaving a sad user - we can
      // optimistically delete, but should revert in case the promise triggered
      // by `sendMessage` didn't resolve
      quillEditor.setContents([]);
      this.editorContentLocal.files = [];
      this.emitChangedLocalContent();
    },
    uploading(status) {
      this.uploadInProgress = status;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../style/main";

.editor-quill-wrap {
  padding: 24px;

  // enforce text direction to be ltr
  :deep(.ql-editor) {
    direction: ltr;
    text-align: left;
  }
}

.actions {
  margin-top: 16px;
  display: flex;
  justify-content: space-between;

  @include small-down {
    flex-direction: column;
  }
}

.button.send-messages {
  @include small-down {
    display: block;
    margin-top: 8px;
  }
}

.upload {
  width: 100%;
  margin-right: 16px;
}

:deep(.dropzone) {
  border-radius: 8px;
  height: 40px;
}

.ai-enabled:deep(div.ql-toolbar.ql-snow) {
  margin-left: 42px !important;
}

:deep(div.ql-toolbar.ql-snow) {
  border: 0;
  padding: 0 0 12px 0;
}

:deep(div.ql-toolbar.ql-snow + .ql-container.ql-snow) {
  border: 1px solid #ddd;
  border-radius: 12px;
}

:deep(div.ql-container) {
  font-family: "Avenir Next", sans-serif;
  height: auto;
}

:deep(div.ql-editor) {
  height: 140px;

  :deep(div.ql-editor strong, div.ql-editor b) {
    font-weight: 600;
  }

  :deep(div.ql-editor i, div.ql-editor em) {
    font-style: italic;
  }
}

@include small-down {

  :deep(div.ql-editor) {
    height: 60px;
  }
}

.chat-disabled {
  pointer-events: none;
  user-select: none;

  & :deep(.ql-toolbar) {
    opacity: .3;
  }

  & :deep(.actions) {
    opacity: .7;
  }

  & .ql-container {
    background-color: $color-grey-tinted-75;
    height: 50px;
    border: 0 !important;
  }

  & :deep(.ql-editor p) {
    color: $color-grey-tinted-500;
  }
}
</style>
